import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import UserImg from "../../assets/User.png";
import FormInput from "../../components/ui/FormInput";
import FormSelect from "../../components/ui/FormSelect";
import { API_STORAGE } from "../../config/api";
import { UserAPI, DataAPI } from "../../services/api";
import { getNationalityOptions } from "../../data/nationalities";
import { COMPETITION_YEAR } from "../../config/competition";

interface ProfileData {
  full_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  gender: string;
  nationality: string;
  id_card_no?: string;
  bio: string;
  profile_image: string;
  id_card_front?: string;
  id_card_back?: string;
  social_links: {
    facebook?: string;
    instagram?: string;
    linkedin?: string;
    youtube?: string;
    tiktok?: string;
    dribbble?: string;
    behance?: string;
    thread?: string;
  };
  pdf_portfolio: string;
  user_type?: "student" | "professional";
  // Student fields
  school_name?: string;
  field_of_study?: string;
  student_id?: string;
  graduation_date?: string;
  student_email?: string;
  student_id_image?: string;
  // Professional fields
  company_name?: string;
  job_title?: string;
  start_date?: string;
  work_email?: string;
  company_website?: string;
}

export function ProfileDashboard() {
  // Helper functions for date formatting
  const formatDateForDisplay = (isoDate: string): string => {
    try {
      // If already in DD/MM/YYYY format, return as-is
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(isoDate)) {
        return isoDate;
      }

      const date = new Date(isoDate);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "";
      }

      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return "";
    }
  };

  const formatDateForBackend = (ddmmyyyy: string): string => {
    try {
      // Convert DD/MM/YYYY to YYYY-MM-DD for backend
      const [day, month, year] = ddmmyyyy.split("/");
      if (
        day &&
        month &&
        year &&
        day.length === 2 &&
        month.length === 2 &&
        year.length === 4
      ) {
        return `${year}-${month}-${day}`;
      }
      return ddmmyyyy; // Return as-is if not in expected format
    } catch {
      return ddmmyyyy;
    }
  };

  // Helper functions for step completion and time formatting
  const getStepCompletionStatus = (step: number): boolean => {
    const validation = validateStep(step);
    return validation.isValid;
  };

  const formatTimeSince = (date: Date | null): string => {
    if (!date) return "Not saved yet";

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60)
      return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  };

  const getStepStatusMessage = (step: number): string => {
    const isCompleted = getStepCompletionStatus(step);
    if (isCompleted) {
      return "You have completed this step.";
    } else {
      const validation = validateStep(step);
      const errorCount = validation.errors.length;
      return `${errorCount} field${errorCount > 1 ? "s" : ""} need${
        errorCount === 1 ? "s" : ""
      } to be completed.`;
    }
  };

  // Guidelines Status Component
  const GuidelinesStatus = ({ step }: { step: number }) => {
    const isCompleted = getStepCompletionStatus(step);
    const statusMessage = getStepStatusMessage(step);
    const timeMessage = formatTimeSince(lastSavedTime);

    return (
      <div className="flex items-start space-x-3 mt-4 sm:mt-6">
        <div
          className={`w-5 h-5 ${
            isCompleted ? "bg-green-600" : "bg-yellow-500"
          } rounded-full flex items-center justify-center`}
        >
          <span className="text-white text-xs">{isCompleted ? "✓" : "!"}</span>
        </div>
        <div className="text-sm">
          <div
            className={`font-medium ${
              isCompleted ? "text-green-800" : "text-yellow-800"
            }`}
          >
            {statusMessage}
          </div>
          <div className={isCompleted ? "text-green-600" : "text-yellow-600"}>
            {timeMessage}
          </div>
        </div>
      </div>
    );
  };
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isProfileCompleted, setIsProfileCompleted] = useState(false);
  const navigate = useNavigate();
  const [showIdCardPopup, setShowIdCardPopup] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [idCardFront, setIdCardFront] = useState<File | null>(null);
  const [idCardBack, setIdCardBack] = useState<File | null>(null);
  const [hasNewIdCards, setHasNewIdCards] = useState(false);
  const [studentIdFile, setStudentIdFile] = useState<File | null>(null);
  const [hasNewStudentIdFile, setHasNewStudentIdFile] = useState(false);
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [hasNewCvFile, setHasNewCvFile] = useState(false);
  const [idCardsUploaded, setIdCardsUploaded] = useState(false);
  const [frontImagePreview, setFrontImagePreview] = useState<string | null>(
    null,
  );
  const [backImagePreview, setBackImagePreview] = useState<string | null>(null);
  const [showStudentIdPopup, setShowStudentIdPopup] = useState(false);
  const [studentIdUploaded, setStudentIdUploaded] = useState(false);
  const [studentIdPreview, setStudentIdPreview] = useState<string | null>(null);
  const [showGuidelinesPopup, setShowGuidelinesPopup] = useState(false);

  // Reusable Action Buttons Component
  const ActionButtons = ({ className = "" }: { className?: string }) => {
    // If profile is completed and we're on step 4, show different buttons
    if (isProfileCompleted && currentStep === 4) {
      return (
        <div className={`flex gap-2 sm:gap-3 ${className}`}>
          <button
            onClick={prevStep}
            className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm min-h-[44px] touch-manipulation"
          >
            Back
          </button>
          <button
            onClick={() => navigate("/dashboard/registration")}
            className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm min-h-[44px] touch-manipulation"
          >
            Your Registration
          </button>
        </div>
      );
    }

    // Default button layout
    return (
      <div className={`flex gap-2 sm:gap-3 ${className}`}>
        <button
          onClick={prevStep}
          disabled={currentStep === 1}
          className={`flex-1 px-3 sm:px-4 py-3 rounded-lg font-medium text-sm transition-opacity min-h-[44px] touch-manipulation ${
            currentStep === 1
              ? "bg-[#B5AEAD] text-white opacity-50 cursor-not-allowed"
              : "bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white hover:opacity-90"
          }`}
        >
          Back
        </button>
        <button
          onClick={saveStep}
          disabled={loading}
          className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm disabled:opacity-50 min-h-[44px] touch-manipulation"
        >
          {loading ? "Saving..." : "Save"}
        </button>
        <button
          onClick={nextStep}
          disabled={currentStep === 4}
          className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] touch-manipulation"
        >
          {currentStep === 4 ? "Finish" : "Next"}
        </button>
      </div>
    );
  };

  // Company and School data
  const [companies, setCompanies] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [schools, setSchools] = useState<Array<{ id: number; name: string }>>(
    [],
  );

  // Last saved time tracking
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);

  // Profile form data
  const [profileData, setProfileData] = useState<ProfileData>({
    full_name: "",
    email: "",
    phone: "",
    date_of_birth: "",
    gender: "",
    nationality: "",
    id_card_no: "",
    bio: "",
    profile_image: "",
    social_links: {},
    pdf_portfolio: "",
    user_type: "student",
  });

  // Load user profile data on component mount
  useEffect(() => {
    loadProfileData();
    loadCompanies();
    loadSchools();
  }, []);

  const loadCompanies = async () => {
    try {
      const data = await DataAPI.getCompanies();
      if (data.success) {
        setCompanies(data.companies);
      }
    } catch (err) {
      console.error("Failed to load companies:", err);
    }
  };

  const loadSchools = async () => {
    try {
      const data = await DataAPI.getSchools();
      if (data.success) {
        setSchools(data.schools);
      }
    } catch (err) {
      console.error("Failed to load schools:", err);
    }
  };

  const loadProfileData = async () => {
    setLoading(true);
    try {
      const data = await UserAPI.getProfile();
      if (data.success && data.user) {
        setProfileData((prev) => ({
          ...prev,
          ...data.user,
          social_links: data.user.social_links || {},
          // Format dates for display (convert from ISO string to DD/MM/YYYY)
          date_of_birth: data.user.date_of_birth
            ? formatDateForDisplay(data.user.date_of_birth)
            : "",
          graduation_date: data.user.graduation_date
            ? formatDateForDisplay(data.user.graduation_date)
            : "",
          start_date: data.user.start_date
            ? formatDateForDisplay(data.user.start_date)
            : "",
        }));

        // Load existing images if they exist
        if (data.user.id_card_front && data.user.id_card_back) {
          setIdCardsUploaded(true);
          setFrontImagePreview(
            `${API_STORAGE.ID_CARDS}${data.user.id_card_front}`,
          );
          setBackImagePreview(
            `${API_STORAGE.ID_CARDS}${data.user.id_card_back}`,
          );
        }

        if (data.user.student_id_image) {
          setStudentIdUploaded(true);
          setStudentIdPreview(
            `${API_STORAGE.STUDENT_IDS}${data.user.student_id_image}`,
          );
        }

        // CV file reference (no preview needed for PDF)
        if (data.user.pdf_portfolio) {
          // Set a flag or filename for CV display - don't set actual file
          // This prevents uploading existing files
          setCvFile({ name: data.user.pdf_portfolio } as File);
          setHasNewCvFile(false); // Mark as existing file, not new
        }

        // Set last saved time from user's updated_at timestamp
        if (data.user.updated_at) {
          setLastSavedTime(new Date(data.user.updated_at));
        }
      }
    } catch (err: any) {
      console.error("Failed to load profile data:", err);
      setError(err.message || "Failed to load profile data");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ProfileData, value: string) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSocialLinkChange = (field: string, value: string) => {
    setProfileData((prev) => ({
      ...prev,
      social_links: {
        ...prev.social_links,
        [field]: value,
      },
    }));
  };

  const showSuccessNotification = (message: string) => {
    setNotificationMessage(message);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const handleFileUpload = (file: File, type: "front" | "back") => {
    if (file.size > 2 * 1024 * 1024) {
      setError("File size must be less than 2MB");
      return;
    }

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/heic"];
    if (!allowedTypes.includes(file.type)) {
      setError("Only JPEG, PNG, and HEIC files are allowed");
      return;
    }

    // Create image preview
    const reader = new FileReader();
    reader.onload = (e) => {
      if (type === "front") {
        setFrontImagePreview(e.target?.result as string);
      } else {
        setBackImagePreview(e.target?.result as string);
      }
    };
    reader.readAsDataURL(file);

    if (type === "front") {
      setIdCardFront(file);
    } else {
      setIdCardBack(file);
    }
    setHasNewIdCards(true); // Mark as new files
    setError("");
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, type: "front" | "back") => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0], type);
    }
  };

  const handleFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "front" | "back",
  ) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0], type);
    }
  };

  const saveIdCards = () => {
    if (!idCardFront || !idCardBack) {
      setError("Please upload both front and back of your ID card");
      return;
    }

    // Just save in state and update UI
    setIdCardsUploaded(true);
    setShowIdCardPopup(false);
    showSuccessNotification("ID cards saved successfully!");
    setError("");
  };

  const handleStudentIdUpload = (file: File) => {
    if (file.size > 2 * 1024 * 1024) {
      setError("File size must be less than 2MB");
      return;
    }

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/heic"];
    if (!allowedTypes.includes(file.type)) {
      setError("Only JPEG, PNG, and HEIC files are allowed");
      return;
    }

    // Create image preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setStudentIdPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    setStudentIdFile(file);
    setHasNewStudentIdFile(true); // Mark as new file
    setError("");
  };

  const handleStudentIdDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleStudentIdUpload(files[0]);
    }
  };

  const handleStudentIdFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleStudentIdUpload(files[0]);
    }
  };

  const saveStudentId = () => {
    if (!studentIdFile) {
      setError("Please upload your student ID");
      return;
    }

    // Just save in state and update UI
    setStudentIdUploaded(true);
    setShowStudentIdPopup(false);
    showSuccessNotification("Student ID saved successfully!");
    setError("");
  };

  const handleCvUpload = (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      setError("File size must be less than 10MB");
      return;
    }

    const allowedTypes = ["application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      setError("Only PDF files are allowed");
      return;
    }

    setCvFile(file);
    setHasNewCvFile(true); // Mark as new file
    setError("");
    showSuccessNotification(`CV uploaded: ${file.name}`);
  };

  const handleCvDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleCvDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleCvUpload(files[0]);
    }
  };

  const handleCvFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleCvUpload(files[0]);
    }
  };

  const saveStep = async () => {
    setLoading(true);
    setError("");

    try {
      // First save the profile data with date format conversion
      const dataToSend = {
        ...profileData,
        date_of_birth: profileData.date_of_birth
          ? formatDateForBackend(profileData.date_of_birth)
          : "",
        graduation_date: profileData.graduation_date
          ? formatDateForBackend(profileData.graduation_date)
          : "",
        start_date: profileData.start_date
          ? formatDateForBackend(profileData.start_date)
          : "",
      };

      const data = await UserAPI.updateProfile(dataToSend);

      if (data.success) {
        let uploadMessages = [];

        // If ID cards are selected and are new files, upload them now
        if (idCardFront && idCardBack && idCardsUploaded && hasNewIdCards) {
          try {
            const formData = new FormData();
            formData.append("id_card_front", idCardFront);
            formData.append("id_card_back", idCardBack);

            const uploadData = await UserAPI.uploadIdCard(formData);
            if (uploadData.success) {
              uploadMessages.push("ID cards");
              // Clear the files after successful upload
              setIdCardFront(null);
              setIdCardBack(null);
              setFrontImagePreview(null);
              setBackImagePreview(null);
              setHasNewIdCards(false);
            } else {
              setError(
                `ID card upload failed: ${
                  uploadData.message || "Unknown error"
                }`,
              );
              setLoading(false);
              return;
            }
          } catch (err: any) {
            console.error("ID card upload failed:", err);
            setError(
              `ID card upload failed: ${err.message || "Network error"}`,
            );
            setLoading(false);
            return;
          }
        }

        // If student ID is selected and is a new file, upload it now
        if (studentIdFile && studentIdUploaded && hasNewStudentIdFile) {
          try {
            const formData = new FormData();
            formData.append("student_id", studentIdFile);

            const uploadData = await UserAPI.uploadStudentId(formData);
            if (uploadData.success) {
              uploadMessages.push("student ID");
              // Clear the file after successful upload
              setStudentIdFile(null);
              setStudentIdPreview(null);
              setHasNewStudentIdFile(false);
            }
          } catch (err: any) {
            console.error("Student ID upload failed:", err);
            // Don't fail the entire save process for student ID upload
          }
        }

        // If CV is selected and is a new file, upload it now
        if (cvFile && hasNewCvFile) {
          try {
            const formData = new FormData();
            formData.append("cv", cvFile);

            const uploadData = await UserAPI.uploadCV(formData);
            if (uploadData.success) {
              uploadMessages.push("CV");
              // Clear the file after successful upload
              setCvFile(null);
              setHasNewCvFile(false);
            }
          } catch (err: any) {
            console.error("CV upload failed:", err);
            // Don't fail the entire save process for CV upload
          }
        }

        // Update last saved time
        setLastSavedTime(new Date());

        // Show appropriate success message
        if (uploadMessages.length > 0) {
          showSuccessNotification(
            `Profile and ${uploadMessages.join(", ")} saved successfully!`,
          );
        } else {
          showSuccessNotification("Profile saved successfully!");
        }

        // If this is the last step (step 4), mark profile as completed
        if (currentStep === 4) {
          setIsProfileCompleted(true);
        }
      } else {
        setError(data.message || "Failed to save profile");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const validateStep = (
    step: number,
  ): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    switch (step) {
      case 1: // Tell us about you
        if (!profileData.full_name?.trim())
          errors.push("Full name is required");
        if (!profileData.email?.trim()) errors.push("Email is required");
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email)) {
          errors.push("Please enter a valid email address");
        }
        if (!profileData.phone?.trim()) errors.push("Phone number is required");
        else if (!/^\d{10}$/.test(profileData.phone)) {
          errors.push("Please enter a valid 10-digit phone number");
        }
        if (!profileData.date_of_birth?.trim())
          errors.push("Date of birth is required");
        if (!profileData.gender?.trim()) errors.push("Gender is required");
        if (!profileData.nationality?.trim())
          errors.push("Nationality is required");
        if (!profileData.id_card_no?.trim())
          errors.push(
            profileData.nationality === "vietnamese"
              ? "Citizen ID (CCCD) number is required"
              : "ID / Passport number is required",
          );
        else if (
          profileData.nationality === "vietnamese" &&
          !/^\d{12}$/.test(profileData.id_card_no.replace(/\s+/g, ""))
        )
          errors.push("Citizen ID (CCCD) number must be exactly 12 digits");
        else if (
          profileData.nationality !== "vietnamese" &&
          !/^[A-Za-z0-9]{6,20}$/.test(profileData.id_card_no.replace(/\s+/g, ""))
        )
          errors.push("ID / Passport number must be 6-20 letters or digits");
        break;

      case 2: // Occupation
        if (!profileData.user_type?.trim())
          errors.push("User type is required");

        if (profileData.user_type === "student") {
          if (!profileData.school_name?.trim())
            errors.push("School name is required");
          if (!profileData.field_of_study?.trim())
            errors.push("Field of study is required");
          if (!profileData.student_id?.trim())
            errors.push("Student ID is required");
          if (!profileData.graduation_date?.trim())
            errors.push("Graduation date is required");
          if (!profileData.student_email?.trim())
            errors.push("Student email is required");
          else if (
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.student_email)
          ) {
            errors.push("Please enter a valid student email address");
          }
        } else if (profileData.user_type === "professional") {
          if (!profileData.company_name?.trim())
            errors.push("Company name is required");
          if (!profileData.job_title?.trim())
            errors.push("Job title is required");
          if (!profileData.start_date?.trim())
            errors.push("Start date is required");
          if (!profileData.work_email?.trim())
            errors.push("Work email is required");
          else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.work_email)) {
            errors.push("Please enter a valid work email address");
          }
        }
        break;

      case 3: // Social connection
        // Facebook is required
        if (!profileData.social_links?.facebook?.trim()) {
          errors.push("Facebook is required");
        }

        // Social links are optional (except Facebook), but if provided, must be valid URLs
        const socialLinks = profileData.social_links || {};
        const urlPattern =
          /^(https?:\/\/)?([a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(\/.*)?$/;

        Object.entries(socialLinks).forEach(([platform, url]) => {
          const trimmedUrl = url && url.trim();
          if (trimmedUrl && !urlPattern.test(trimmedUrl)) {
            errors.push(`Please enter a valid URL for ${platform}`);
          }
        });
        break;

      case 4: // Polish your profile
        // This step is optional, but bio should be reasonable length if provided
        if (profileData.bio && profileData.bio.length > 1000) {
          errors.push("Bio must be less than 1000 characters");
        }
        break;
    }

    return { isValid: errors.length === 0, errors };
  };

  const nextStep = () => {
    const validation = validateStep(currentStep);

    if (!validation.isValid) {
      setError(validation.errors.join(". "));
      return;
    }

    setError(""); // Clear any previous errors
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getStepLabel = (step: number) => {
    switch (step) {
      case 1:
        return "Tell us about you";
      case 2:
        return "Occupation";
      case 3:
        return "Social connection";
      case 4:
        return "Polish your profile";
      default:
        return "";
    }
  };

  const getProgressPercent = () => {
    switch (currentStep) {
      case 1:
        return 30;
      case 2:
        return 50;
      case 3:
        return 70;
      case 4:
        return 100;
      default:
        return 30;
    }
  };

  const renderStep1 = () => (
    <div className="flex flex-col lg:flex-row gap-8 bg-[#EDEDED] rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm">
      {/* Left Column - Form */}
      <div className="flex-1">
        {/* Basic Information Header */}
        <div className="flex items-center gap-3 mb-6 lg:mb-8">
          <div className="w-5 sm:w-6 flex items-center justify-center">
            <img src={UserImg} alt="User Profile" className="w-full" />
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            Basic infomation
          </h2>
        </div>

        {/* Form Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
          <FormInput
            label="Full name"
            placeholder="John Smith"
            value={profileData.full_name}
            onChange={(value) => handleInputChange("full_name", value)}
            required
          />

          <FormInput
            label="Day of birth"
            placeholder="dd/mm/yyyy"
            value={profileData.date_of_birth}
            onChange={(value) => handleInputChange("date_of_birth", value)}
            required
          />

          <FormInput
            label="Email"
            type="email"
            placeholder="email@example.com"
            value={profileData.email}
            onChange={(value) => handleInputChange("email", value)}
            disabled
            required
          />

          <FormInput
            label="Phone"
            type="tel"
            placeholder="0909 123 456"
            value={profileData.phone}
            onChange={(value) => handleInputChange("phone", value)}
            required
          />

          <FormSelect
            label="Gender"
            placeholder="Select gender"
            value={profileData.gender}
            onChange={(value) => handleInputChange("gender", value)}
            options={[
              { value: "male", label: "Male" },
              { value: "female", label: "Female" },
              { value: "other", label: "Other" },
            ]}
            required
          />

          <FormSelect
            label="Nationality"
            value={profileData.nationality}
            onChange={(value) => handleInputChange("nationality", value)}
            options={getNationalityOptions()}
            placeholder="Select your nationality"
            required
          />

          <FormInput
            label={
              profileData.nationality === "vietnamese" || !profileData.nationality
                ? "Citizen ID (CCCD) number"
                : "ID / Passport number"
            }
            placeholder={
              profileData.nationality === "vietnamese" || !profileData.nationality
                ? "12 digits, e.g. 079123456789"
                : "Passport number"
            }
            value={profileData.id_card_no || ""}
            onChange={(value) =>
              handleInputChange("id_card_no", value.replace(/\s+/g, "").toUpperCase())
            }
            required
          />
        </div>

        {/* Student/Professional Selection */}
        <div className="mb-6 lg:mb-8">
          <div className="flex flex-col sm:grid sm:grid-cols-2 gap-4 sm:gap-6">
            {/* Left Column - Checkboxes */}
            <div className="flex flex-col gap-4">
              <label className="flex items-center gap-3">
                <input
                  type="radio"
                  name="user_type"
                  value="student"
                  checked={profileData.user_type === "student"}
                  onChange={(e) =>
                    handleInputChange("user_type", e.target.value)
                  }
                  className="w-5 h-5 text-gray-800 border-gray-300 focus:ring-gray-800"
                />
                <span className="text-sm text-gray-700">I'm a student</span>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="radio"
                  name="user_type"
                  value="professional"
                  checked={profileData.user_type === "professional"}
                  onChange={(e) =>
                    handleInputChange("user_type", e.target.value)
                  }
                  className="w-5 h-5 text-gray-800 border-gray-300 focus:ring-gray-800"
                />
                <span className="text-sm text-gray-700">
                  I'm a working professional
                </span>
              </label>
            </div>

            {/* Right Column - Upload ID Card Button */}
            <div className="flex items-center mt-4 sm:mt-0">
              {idCardsUploaded ? (
                <div className="text-left text-sm font-medium">
                  <p className="text-green-800">ID Card images uploaded.</p>
                  <p className="text-gray-700">
                    If you changed your mind,{" "}
                    <span
                      className="text-base font-semibold text-gray-900 cursor-pointer"
                      onClick={() => setShowIdCardPopup(true)}
                    >
                      upload again
                    </span>
                  </p>
                </div>
              ) : (
                <button
                  onClick={() => setShowIdCardPopup(true)}
                  className="bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white py-4 px-4 rounded-lg hover:opacity-90 transition-opacity font-medium text-sm h-16 flex items-center justify-center w-full min-h-[44px] touch-manipulation"
                >
                  Upload your ID Card{" "}
                  <span className="text-red-500 ml-1">*</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Right Column - Guidelines */}
      <div className="hidden lg:block w-80 space-y-4 text-left">
        <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm h-fit">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">
            Guidelines
          </h3>

          <div className="space-y-4 text-xs text-gray-600 mb-4 sm:mb-6">
            <p>
              Fields marked <span className="text-red-500">*</span> are
              required.
              <br />
              Use your legal name as it appears on ID.
              <br />
              Keep your bio concise and relevant.
              <br />
              You can edit this info later.
            </p>
          </div>

          {/* Completion Status */}
          <div
            className={`p-3 rounded-lg border ${
              getStepCompletionStatus(currentStep)
                ? "bg-green-50 border-green-200"
                : "bg-yellow-50 border-yellow-200"
            }`}
          >
            <GuidelinesStatus step={currentStep} />
          </div>
        </div>

        {/* Action Buttons - Desktop Only */}
        <ActionButtons />
      </div>
    </div>
  );

  const renderStep2Student = () => (
    <div className="flex flex-col lg:flex-row gap-8 bg-[#EDEDED] rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm">
      {/* Left Column - Form */}
      <div className="flex-1">
        {/* Student Information Header */}
        <div className="flex items-center gap-3 mb-6 lg:mb-8">
          <div className="w-5 sm:w-6 flex items-center justify-center">
            <img src={UserImg} alt="User Profile" className="w-full" />
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            Student information
          </h2>
        </div>

        {/* Form Fields */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <div className="col-span-2">
            <FormSelect
              label="School name"
              placeholder="School name"
              value={profileData.school_name || ""}
              onChange={(value) => handleInputChange("school_name", value)}
              options={schools
                .map((school) => ({
                  value: school.name,
                  label: school.name,
                }))
                .concat(
                  !schools.find((s) => s.name === "Other")
                    ? [
                        {
                          value: "Other",
                          label: "Other (please specify in comments)",
                        },
                      ]
                    : [],
                )}
              required
            />
          </div>

          <div className="col-span-2">
            <FormInput
              label="Field of Study"
              placeholder="Chief Creative Officer"
              value={profileData.field_of_study || ""}
              onChange={(value) => handleInputChange("field_of_study", value)}
              required
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
          <FormInput
            label="StudentID"
            placeholder="123456"
            value={profileData.student_id || ""}
            onChange={(value) => handleInputChange("student_id", value)}
            required
          />

          <FormInput
            label="Graduation Date"
            placeholder="DD/MM/YYYY"
            value={profileData.graduation_date || ""}
            onChange={(value) => handleInputChange("graduation_date", value)}
            required
          />

          <FormInput
            label="Student Email"
            type="email"
            placeholder="name@company.com"
            value={profileData.student_email || ""}
            onChange={(value) => handleInputChange("student_email", value)}
            required
          />

          <div className="flex items-center mt-6">
            {studentIdUploaded ? (
              <div className="text-left text-sm font-medium w-full">
                <p className="text-green-800">Student ID uploaded.</p>
                <p className="text-gray-700">
                  If you changed your mind,{" "}
                  <span
                    className="text-base font-semibold text-gray-900 cursor-pointer"
                    onClick={() => setShowStudentIdPopup(true)}
                  >
                    upload again
                  </span>
                </p>
              </div>
            ) : (
              <button
                onClick={() => setShowStudentIdPopup(true)}
                className="bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white py-3 px-4 rounded-lg hover:opacity-90 transition-opacity font-medium text-sm w-full min-h-[44px] touch-manipulation"
              >
                Upload student ID <span className="text-red-500 ml-1">*</span>
              </button>
            )}
          </div>

          {/* Graduation Date Warning Message */}
          {profileData.graduation_date &&
            (() => {
              try {
                // Parse DD/MM/YYYY format
                const parts = profileData.graduation_date.split("/");
                if (parts.length === 3) {
                  const day = parseInt(parts[0], 10);
                  const month = parseInt(parts[1], 10);
                  const year = parseInt(parts[2], 10);

                  // Create date object (month is 0-indexed)
                  const graduationDate = new Date(year, month - 1, day);
                  const targetDate = new Date(COMPETITION_YEAR, 0, 1); // January 1 of the competition year

                  // Check if graduation date is valid and before 01/01 of the competition year
                  if (
                    !isNaN(graduationDate.getTime()) &&
                    graduationDate < targetDate
                  ) {
                    return (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg
                              className="h-5 w-5 text-red-400"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path
                                fillRule="evenodd"
                                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-red-700 font-medium">
                              If you graduate before 01/01/{COMPETITION_YEAR},
                              please select the Industry Practitioner league.
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                }
              } catch (error) {
                // Ignore parsing errors
              }
              return null;
            })()}
        </div>

        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Right Column - Guidelines */}
      <div className="hidden lg:block w-80 space-y-4 text-left">
        <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm h-fit">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">
            Guidelines
          </h3>

          <div className="space-y-4 text-sm text-gray-600 mb-4 sm:mb-6">
            <p>
              Fields marked <span className="text-red-500">*</span> are
              required.
              <br />
              Use your legal name as it appears on ID.
              <br />
              Keep your bio concise and relevant.
              <br />
              You can edit this info later.
            </p>
          </div>

          {/* Completion Status */}
          <div
            className={`p-3 rounded-lg border ${
              getStepCompletionStatus(currentStep)
                ? "bg-green-50 border-green-200"
                : "bg-yellow-50 border-yellow-200"
            }`}
          >
            <GuidelinesStatus step={currentStep} />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 sm:gap-3">
          <button
            onClick={prevStep}
            className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm min-h-[44px] touch-manipulation"
          >
            Back
          </button>
          <button
            onClick={saveStep}
            disabled={loading}
            className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save"}
          </button>
          <button
            onClick={nextStep}
            className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm min-h-[44px] touch-manipulation"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );

  const renderStep2Professional = () => (
    <div className="flex flex-col lg:flex-row gap-8 bg-[#EDEDED] rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm">
      {/* Left Column - Form */}
      <div className="flex-1">
        {/* Professional Information Header */}
        <div className="mb-6 lg:mb-8">
          <div className="flex items-center gap-3">
            <div className="w-5 sm:w-6 flex items-center justify-center">
              <img src={UserImg} alt="User Profile" className="w-full" />
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 text-left">
              Working Professional Information
            </h2>
          </div>
          <p className="mt-1 text-xs sm:text-sm italic text-gray-600 text-left">
            If you cannot find your school or company name in the list, please
            contact the Organizing Committee at{" "}
            <a
              href="mailto:competition@aimacademy.vn"
              className="underline hover:text-gray-900"
            >
              competition@aimacademy.vn
            </a>{" "}
            for assistance.
          </p>
        </div>

        {/* Form Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="col-span-2">
            <FormSelect
              label="Company name"
              placeholder="Company name"
              value={profileData.company_name || ""}
              onChange={(value) => handleInputChange("company_name", value)}
              options={companies.map((company) => ({
                value: company.name,
                label: company.name,
              }))}
              required
            />
            {/* Freelancer Warning Message */}
            {profileData.company_name?.toLowerCase() === "freelancer" && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <div className="ml-3">
                    <p className="text-sm text-red-700 font-medium">
                      Freelancers and agency professionals are not eligible for
                      the Marketers – Industry Practitioner league. For
                      assistance, please contact the hotline 093 1333 150 (ext.
                      02).
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <FormInput
            label="Job Title"
            placeholder="Chief Creative Officer"
            value={profileData.job_title || ""}
            onChange={(value) => handleInputChange("job_title", value)}
            required
          />

          <FormInput
            label="Start date"
            placeholder="DD/MM/YYYY"
            value={profileData.start_date || ""}
            onChange={(value) => handleInputChange("start_date", value)}
            required
          />
          <div>
            <FormInput
              label="Work email"
              type="email"
              placeholder="name@company.com"
              value={profileData.work_email || ""}
              onChange={(value) => handleInputChange("work_email", value)}
              required
            />
            {/* Freelancer Warning Message */}
            {profileData.company_name?.toLowerCase() === "freelancer" && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <div className="ml-3">
                    <p className="text-sm text-red-700 font-medium text-left">
                      If you don’t have a work email, please use your personal
                      email
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <FormInput
            label="Company Website"
            placeholder="www.company.com"
            value={profileData.company_website || ""}
            onChange={(value) => handleInputChange("company_website", value)}
          />
        </div>

        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Right Column - Guidelines */}
      <div className="hidden lg:block w-80 space-y-4 text-left">
        <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm h-fit">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">
            Guidelines
          </h3>

          <div className="space-y-4 text-sm text-gray-600 mb-4 sm:mb-6">
            <p>
              Fields marked <span className="text-red-500">*</span> are
              required.
              <br />
              Use your legal name as it appears on ID.
              <br />
              Keep your bio concise and relevant.
              <br />
              You can edit this info later.
            </p>
          </div>

          {/* Completion Status */}
          <div
            className={`p-3 rounded-lg border ${
              getStepCompletionStatus(currentStep)
                ? "bg-green-50 border-green-200"
                : "bg-yellow-50 border-yellow-200"
            }`}
          >
            <GuidelinesStatus step={currentStep} />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 sm:gap-3">
          <button
            onClick={prevStep}
            className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm min-h-[44px] touch-manipulation"
          >
            Back
          </button>
          <button
            onClick={saveStep}
            disabled={loading}
            className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save"}
          </button>
          <button
            onClick={nextStep}
            className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm min-h-[44px] touch-manipulation"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="flex flex-col lg:flex-row gap-8 bg-[#EDEDED] rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm">
      {/* Left Column - Form */}
      <div className="flex-1">
        {/* Social Connection Header */}
        <div className="flex items-center gap-3 mb-6 lg:mb-8">
          <div className="w-5 sm:w-6 flex items-center justify-center">
            <img src={UserImg} alt="User Profile" className="w-full" />
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            Social connection
          </h2>
        </div>

        {/* Form Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <FormInput
            label="Facebook"
            placeholder="https://"
            value={profileData.social_links?.facebook || ""}
            onChange={(value) => handleSocialLinkChange("facebook", value)}
            required
          />

          <FormInput
            label="LinkedIn"
            placeholder="https://"
            value={profileData.social_links?.linkedin || ""}
            onChange={(value) => handleSocialLinkChange("linkedin", value)}
          />

          <FormInput
            label="YouTube"
            placeholder="https://"
            value={profileData.social_links?.youtube || ""}
            onChange={(value) => handleSocialLinkChange("youtube", value)}
          />

          <FormInput
            label="TikTok"
            placeholder="https://"
            value={profileData.social_links?.tiktok || ""}
            onChange={(value) => handleSocialLinkChange("tiktok", value)}
          />

          <FormInput
            label="Dribbble"
            placeholder="https://"
            value={profileData.social_links?.dribbble || ""}
            onChange={(value) => handleSocialLinkChange("dribbble", value)}
          />

          <FormInput
            label="Behance"
            placeholder="https://"
            value={profileData.social_links?.behance || ""}
            onChange={(value) => handleSocialLinkChange("behance", value)}
          />

          <FormInput
            label="Instagram"
            placeholder="https://"
            value={profileData.social_links?.instagram || ""}
            onChange={(value) => handleSocialLinkChange("instagram", value)}
          />

          <FormInput
            label="Thread"
            placeholder="https://"
            value={profileData.social_links?.thread || ""}
            onChange={(value) => handleSocialLinkChange("thread", value)}
          />
        </div>

        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Right Column - Guidelines */}
      <div className="hidden lg:block w-80 space-y-4 text-left">
        <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm h-fit">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">
            Guidelines
          </h3>

          <div className="space-y-4 text-sm text-gray-600 mb-4 sm:mb-6">
            <p>
              Fields marked <span className="text-red-500">*</span> are
              required.
              <br />
              Use your legal name as it appears on ID.
              <br />
              Keep your bio concise and relevant.
              <br />
              You can edit this info later.
            </p>
          </div>

          {/* Completion Status */}
          <div
            className={`p-3 rounded-lg border ${
              getStepCompletionStatus(currentStep)
                ? "bg-green-50 border-green-200"
                : "bg-yellow-50 border-yellow-200"
            }`}
          >
            <GuidelinesStatus step={currentStep} />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 sm:gap-3">
          <button
            onClick={prevStep}
            className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm min-h-[44px] touch-manipulation"
          >
            Back
          </button>
          <button
            onClick={saveStep}
            disabled={loading}
            className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save"}
          </button>
          <button
            onClick={nextStep}
            className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm min-h-[44px] touch-manipulation"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="flex flex-col lg:flex-row gap-8 bg-[#EDEDED] rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm">
      {/* Left Column - Form */}
      <div className="flex-1">
        {/* Polish Profile Header */}
        <div className="flex items-center gap-3 mb-6 lg:mb-8">
          <div className="w-5 sm:w-6 flex items-center justify-center">
            <img src={UserImg} alt="User Profile" className="w-full" />
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            Polish your profile
          </h2>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          You're almost there, it's a good idea to upload your latest CV or
          portfolio
        </p>

        {/* CV Upload Area */}
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-6 lg:p-8 text-center mb-6 cursor-pointer hover:border-gray-400 transition-colors touch-manipulation"
          onDragOver={handleCvDragOver}
          onDrop={handleCvDrop}
          onClick={() => document.getElementById("cvFileInput")?.click()}
        >
          {cvFile ? (
            <div className="text-center">
              <svg
                className="w-8 h-8 text-green-500 mx-auto mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="text-sm text-gray-700 font-medium mb-1">
                {cvFile.name}
              </p>
              <p className="text-xs text-gray-500">
                PDF uploaded. You can upload again
              </p>
            </div>
          ) : (
            <>
              <svg
                className="w-8 h-8 text-gray-400 mx-auto mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <p className="text-sm text-gray-600 mb-2">
                DRAG AND DROP YOUR FILE HERE
              </p>
              <p className="text-xs text-gray-500">
                Or click here to{" "}
                <span className="font-medium">Upload from your computer</span>
              </p>
            </>
          )}
        </div>
        <input
          id="cvFileInput"
          type="file"
          accept="application/pdf"
          onChange={handleCvFileSelect}
          className="hidden"
        />

        {/* Bio Field */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
            Sort Biography <span className="text-red-500">*</span>
          </label>
          <textarea
            placeholder="About myself"
            value={profileData.bio}
            onChange={(e) => handleInputChange("bio", e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-none"
          />
        </div>

        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Right Column - Guidelines */}
      <div className="hidden lg:block w-80 space-y-4 text-left">
        <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm h-fit">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">
            Guidelines
          </h3>

          <div className="space-y-4 text-sm text-gray-600 mb-4 sm:mb-6">
            <p>
              Fields marked <span className="text-red-500">*</span> are
              required.
              <br />
              Use your legal name as it appears on ID.
              <br />
              Keep your bio concise and relevant.
              <br />
              You can edit this info later.
            </p>
          </div>

          {/* Completion Status */}
          <div
            className={`p-3 rounded-lg border ${
              getStepCompletionStatus(currentStep)
                ? "bg-green-50 border-green-200"
                : "bg-yellow-50 border-yellow-200"
            }`}
          >
            <GuidelinesStatus step={currentStep} />
          </div>
        </div>

        {/* Action Buttons */}
        <ActionButtons />
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return profileData.user_type === "student"
          ? renderStep2Student()
          : renderStep2Professional();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      default:
        return renderStep1();
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-0">
        {/* Step Progress Header */}
        <div className="mb-5">
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <div className="text-xs sm:text-sm text-gray-600">
              Step {currentStep} of 4
            </div>
            <div className="text-xs sm:text-sm text-gray-600">
              {getProgressPercent()}% complete
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4 sm:mb-6">
            <div
              className="bg-gray-800 h-2 rounded-full transition-all duration-300"
              style={{ width: `${getProgressPercent()}%` }}
            />
          </div>

          {/* Step Navigation */}
          <div className="flex gap-1 sm:gap-2">
            {[1, 2, 3, 4].map((step) => (
              <button
                key={step}
                className={`flex-1 px-2 sm:px-4 lg:px-6 py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-medium transition-colors min-h-[40px] sm:min-h-[44px] touch-manipulation ${
                  step === currentStep
                    ? "bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white"
                    : "bg-[#B5AEAD] text-white"
                }`}
              >
                <span className="hidden sm:inline">{getStepLabel(step)}</span>
                <span className="sm:hidden">{step}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Error Notification */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Please fix the following errors:
                </h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
              <div className="ml-auto pl-3">
                <button
                  type="button"
                  className="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100"
                  onClick={() => setError("")}
                >
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        {renderStepContent()}

        {/* Mobile Action Buttons - Only visible on mobile/tablet */}
        <div className="lg:hidden mt-8 px-4 sm:px-0">
          <ActionButtons />
        </div>

        {/* ID Card Upload Popup */}
        {showIdCardPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-4 sm:p-6 lg:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
                UPLOAD ID CARD
              </h3>
              <p className="text-sm text-gray-600 mb-6 text-center">
                Please upload photos of the front and back of your ID Card
                <br />
                JPEG, PNG, HEIC · Max 2MB
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6">
                {/* ID Card Front */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3 text-left">
                    ID Card Front (Mặt trước CCCD)
                  </h4>
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-2 text-center h-32 sm:h-48 flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition-colors touch-manipulation"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, "front")}
                    onClick={() =>
                      document.getElementById("frontFileInput")?.click()
                    }
                  >
                    {frontImagePreview ? (
                      <div className="w-full h-full flex flex-col">
                        <img
                          src={frontImagePreview}
                          alt="ID Card Front Preview"
                          className="w-full h-24 sm:h-40 object-cover rounded-lg"
                        />
                        <p className="text-xs text-gray-500 text-center">
                          File uploaded. You can upload again
                        </p>
                      </div>
                    ) : (
                      <>
                        <svg
                          className="w-8 h-8 text-gray-400 mb-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        <p className="text-xs text-gray-600 mb-2">
                          DRAG AND DROP YOUR FILE HERE
                        </p>
                        <p className="text-xs text-gray-500">
                          Or click here to{" "}
                          <span className="font-medium">
                            Upload from your computer
                          </span>
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    id="frontFileInput"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/heic"
                    onChange={(e) => handleFileSelect(e, "front")}
                    className="hidden"
                  />
                </div>

                {/* ID Card Back */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3 text-left">
                    ID Card Back (Mặt sau CCCD)
                  </h4>
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-2 text-center h-32 sm:h-48 flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition-colors touch-manipulation"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, "back")}
                    onClick={() =>
                      document.getElementById("backFileInput")?.click()
                    }
                  >
                    {backImagePreview ? (
                      <div className="w-full h-full flex flex-col">
                        <img
                          src={backImagePreview}
                          alt="ID Card Back Preview"
                          className="w-full h-24 sm:h-40 object-cover rounded-lg"
                        />
                        <p className="text-xs text-gray-500 text-center">
                          File uploaded. You can upload again
                        </p>
                      </div>
                    ) : (
                      <>
                        <svg
                          className="w-8 h-8 text-gray-400 mb-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        <p className="text-xs text-gray-600 mb-2">
                          DRAG AND DROP YOUR FILE HERE
                        </p>
                        <p className="text-xs text-gray-500">
                          Or click here to{" "}
                          <span className="font-medium">
                            Upload from your computer
                          </span>
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    id="backFileInput"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/heic"
                    onChange={(e) => handleFileSelect(e, "back")}
                    className="hidden"
                  />
                </div>
              </div>

              <div className="flex gap-3 sm:gap-4">
                <button
                  onClick={() => setShowIdCardPopup(false)}
                  className="flex-1 px-4 sm:px-6 py-3 bg-gray-800 text-white rounded-lg hover:opacity-90 transition-opacity font-medium min-h-[44px] touch-manipulation"
                >
                  Cancel
                </button>
                <button
                  onClick={saveIdCards}
                  disabled={!idCardFront || !idCardBack}
                  className="flex-1 px-4 sm:px-6 py-3 bg-gray-800 text-white rounded-lg hover:opacity-90 transition-opacity font-medium disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] touch-manipulation"
                >
                  Finish
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Student ID Upload Popup */}
        {showStudentIdPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-4 sm:p-6 lg:p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
                UPLOAD STUDENT ID
              </h3>
              <p className="text-sm text-gray-600 mb-6 text-center">
                Please upload a photo of your student ID
                <br />
                JPEG, PNG, HEIC · Max 2MB
              </p>

              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3 text-left">
                  Student ID
                </h4>
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-2 text-center h-32 sm:h-48 flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition-colors touch-manipulation"
                  onDragOver={handleDragOver}
                  onDrop={handleStudentIdDrop}
                  onClick={() =>
                    document.getElementById("studentIdFileInput")?.click()
                  }
                >
                  {studentIdPreview ? (
                    <div className="w-full h-full flex flex-col">
                      <img
                        src={studentIdPreview}
                        alt="Student ID Preview"
                        className="w-full h-20 sm:h-32 object-cover rounded-lg"
                      />
                      <p className="text-xs text-gray-500 text-center">
                        File uploaded. You can upload again
                      </p>
                    </div>
                  ) : (
                    <>
                      <svg
                        className="w-8 h-8 text-gray-400 mb-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      <p className="text-xs text-gray-600 mb-2">
                        DRAG AND DROP YOUR FILE HERE
                      </p>
                      <p className="text-xs text-gray-500">
                        Or click here to{" "}
                        <span className="font-medium">
                          Upload from your computer
                        </span>
                      </p>
                    </>
                  )}
                </div>
                <input
                  id="studentIdFileInput"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/heic"
                  onChange={handleStudentIdFileSelect}
                  className="hidden"
                />
              </div>

              <div className="flex gap-3 sm:gap-4">
                <button
                  onClick={() => setShowStudentIdPopup(false)}
                  className="flex-1 px-4 sm:px-6 py-3 bg-gray-800 text-white rounded-lg hover:opacity-90 transition-opacity font-medium min-h-[44px] touch-manipulation"
                >
                  Cancel
                </button>
                <button
                  onClick={saveStudentId}
                  disabled={!studentIdFile}
                  className="flex-1 px-4 sm:px-6 py-3 bg-gray-800 text-white rounded-lg hover:opacity-90 transition-opacity font-medium disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] touch-manipulation"
                >
                  Finish
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Floating Help Button - Only visible on mobile/tablet */}
        <div className="lg:hidden fixed bottom-6 right-6 z-40">
          <button
            onClick={() => setShowGuidelinesPopup(true)}
            className="w-12 h-12 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center touch-manipulation"
            aria-label="Show guidelines"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </button>
        </div>

        {/* Guidelines Popup - Mobile/Tablet Only */}
        {showGuidelinesPopup && (
          <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-4 sm:p-6 max-w-sm w-full max-h-[80vh] overflow-y-auto shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Guidelines
                </h3>
                <button
                  onClick={() => setShowGuidelinesPopup(false)}
                  className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 touch-manipulation"
                  aria-label="Close guidelines"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-4 text-sm text-gray-600 mb-6">
                <p>
                  Fields marked <span className="text-red-500">*</span> are
                  required.
                  <br />
                  Use your legal name as it appears on ID.
                  <br />
                  Keep your bio concise and relevant.
                  <br />
                  You can edit this info later.
                </p>
              </div>

              {/* Completion Status */}
              <div
                className={`p-3 rounded-lg border mb-4 ${
                  getStepCompletionStatus(currentStep)
                    ? "bg-green-50 border-green-200"
                    : "bg-yellow-50 border-yellow-200"
                }`}
              >
                <GuidelinesStatus step={currentStep} />
              </div>
            </div>
          </div>
        )}

        {/* Loading Overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
              <p className="text-gray-800 font-medium">
                {currentStep === 1
                  ? "Loading profile data..."
                  : "Saving profile data..."}
              </p>
            </div>
          </div>
        )}

        {/* Success Notification */}
        {showNotification && (
          <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{notificationMessage}</span>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default ProfileDashboard;
